import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import {
  type OverlayFailure,
  drawerHashFallbackHref,
  retryOverlay,
} from "~/components/overlay-retry";

import { parseDrawerHash } from "./drawer-hash";

/**
 * Stand-in listener for a FAILED lazy login/register drawer (PR #1141,
 * COM-189 wave 4 follow-up). The real drawer opens off the URL hash (`#login`,
 * `#register`, the checkout panes) — but that listener lives INSIDE the lazy
 * chunk, so when `account-drawer-*.js` fails to import, every "Sign in" link
 * on the page changes the hash and nothing happens. This component mounts
 * only while the drawer's `OverlayBoundary` is latched and watches the same
 * signals the drawer would (`hashchange` for plain anchors, `location.key` for
 * RR7 soft navs); on an auth hash it retries via `overlay-retry.ts` — a
 * guarded reload with the hash kept (so the drawer opens on arrival), the
 * `/account` page (`?action=register` for the register hash) when the guard is
 * spent, or ONE in-place remount for a non-module render crash. Renders
 * nothing.
 *
 * WHEN IT FIRES WITHOUT A NEW SIGNAL — only for a build-guarded `reload`. The
 * owner mounts a fresh instance for every failure, so an attempt at mount time
 * must not be able to feed the next failure, in this document OR the next:
 *   - a `remount` at mount re-throws synchronously (React.lazy caches a chunk
 *     that evaluates and throws just like a failed import), reports a new
 *     failure, remounts this listener with a fresh `fired` ref and goes again
 *     — an in-document loop;
 *   - a `navigate` at mount lands on a new document that imports the chunk
 *     again; for a deterministic cause (content blocker, CSP, a purged asset)
 *     it fails again, a fresh listener mounts and navigates again — a
 *     cross-document loop, with a Worker document render per lap, that
 *     survives even a spent build guard (sessionStorage is per tab);
 *   - a `reload` is bounded by the build guard (once per build per tab), so a
 *     hash landing with a module-load failure (`/#login` from a magic-link
 *     email or a `requireAccountUser` redirect) may take it at once. With the
 *     guard spent the landing parks — the pre-PR dead drawer — until the
 *     shopper taps, and that tap goes to the full page.
 * The mount-time attempt therefore runs `unattended` (overlay-retry.ts): the
 * planner turns anything but `reload` into `park`. A soft nav to the hash, a
 * `hashchange`, and the escalation right after the shopper's own tap
 * remounted once (`remountAttempted`) are shopper signals and plan normally.
 *
 * A `park` STRIPS the auth hash from the URL (`replaceState`, the same way
 * the drawer does after consuming one). The failed drawer never ran its
 * hash-clearing effect, so a parked landing (`/#login` with the guard spent)
 * would otherwise keep `#login` in the URL — and a plain `<a href="#login">`
 * tap on a same-fragment URL is a same-document navigation the browser
 * answers with NO `hashchange` and NO history entry, so neither signal this
 * listener watches would fire and the tap would be dead (cart.tsx
 * `cart-signin-link`, the header's standalone trigger and the Builder blocks
 * all render plain anchors; only RR7 `<Link>` taps would have worked). With
 * the hash gone the next tap is a real `'' → '#login'` change and carries its
 * own hash for `drawerHashFallbackHref`. `reload` keeps the hash (it acts, so
 * it never parks) and the drawer opens on arrival as before.
 *
 * THE POST-REMOUNT ESCALATION PLANS OFF `remountHash`, NOT THE LIVE HASH. The
 * real drawer's hash effect clears the hash (`replaceState`) in the same
 * effect that schedules `setOpen(true)` — BEFORE the open body renders and
 * throws. So when the tap's remount fails in the open body, the fresh
 * listener mounts with `remountAttempted` set but `window.location.hash`
 * already `''`; read the live hash and it would return before firing, the
 * tap would be a silent remount → crash, and `/account` would only come on
 * the shopper's NEXT tap. The owner therefore captures the hash the remount
 * was planned off (`remountHash`) and the mount-time run with
 * `remountAttempted` falls back to it when the live hash carries no auth
 * target, so the SAME tap escalates. Only that run uses it: a `hashchange` or
 * soft nav always plans off the hash it arrived with.
 */
export function AccountDrawerRetryOnHash({
  failure,
  onRemount,
  remountAttempted = false,
  remountHash,
  reload,
  navigate,
}: {
  failure: OverlayFailure;
  onRemount: () => void;
  /** The owner already remounted the drawer once for this failure and it
   *  failed again; escalates a render crash to `navigate` (overlay-retry.ts).
   *  The owner must clear it once a remounted drawer has rendered OPEN and
   *  survived that commit (`DrawerRendered` below, hosted inside the drawer's
   *  open content), so a later, unrelated crash on the same page gets its own
   *  remount rather than an escalation the shopper never tapped for. */
  remountAttempted?: boolean;
  /** The auth hash the owner's remount was planned off (`window.location.hash`
   *  at `onRemount` time). Used by the mount-time run only, and only when
   *  `remountAttempted` is set and the live hash no longer carries an auth
   *  target — the remounted drawer consumed it before failing (see the
   *  docblock). Cleared by the owner together with `remountAttempted`. */
  remountHash?: string;
  /** Test seams; default to the document reload / assign. */
  reload?: () => void;
  navigate?: (href: string) => void;
}) {
  const location = useLocation();
  // Once per failure instance: a hash tap can reach us twice (the
  // `hashchange` event AND the `location.key` effect re-run), and every
  // outcome that acts (reload / navigate / the parent unmounting us on
  // remount) makes a second attempt for the same failure redundant at best. A
  // `park` did not act and does not spend it.
  const fired = useRef(false);
  useEffect(() => {
    fired.current = false;
  }, [failure]);
  // Which `location.key` the previous effect run saw, so a re-run can tell a
  // soft nav (a shopper signal) from a re-run caused by a new failure or a
  // changed callback (not one).
  const seenKey = useRef(location.key);
  useEffect(() => {
    const isSoftNav = seenKey.current !== location.key;
    seenKey.current = location.key;
    const attempt = (unattended: boolean, consumedHash?: string) => {
      if (fired.current) return;
      const liveHash = window.location.hash;
      // The remounted drawer cleared the hash before it failed: escalate off
      // the hash the remount was planned off (docblock).
      const hash =
        parseDrawerHash(liveHash) === null &&
        consumedHash !== undefined &&
        parseDrawerHash(consumedHash) !== null
          ? consumedHash
          : liveHash;
      if (parseDrawerHash(hash) === null) return;
      fired.current = true;
      const plan = retryOverlay("account-drawer", failure, {
        fallbackHref: drawerHashFallbackHref(hash),
        remountAttempted,
        unattended,
        onRemount,
        reload,
        navigate,
      });
      if (plan.kind === "park") {
        fired.current = false;
        // Consume the hash the failed drawer never did, so the shopper's next
        // same-hash anchor tap is a real `hashchange` (docblock). A `park` is
        // always the unattended mount-time run off the LIVE hash — the
        // `remountHash` run is attended and never parks.
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      }
    };
    // Mount-time run: a soft nav or the post-remount escalation is a shopper
    // signal; a bare hash landing is not, so only a guarded reload may act.
    attempt(
      !isSoftNav && !remountAttempted,
      remountAttempted ? remountHash : undefined,
    );
    const onHashChange = () => attempt(false);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
    // `location.key`, not `.hash`: RR7 soft navs pushState without a
    // `hashchange`, same reason the drawer itself keys on it.
  }, [
    location.key,
    failure,
    onRemount,
    remountAttempted,
    remountHash,
    reload,
    navigate,
  ]);
  return null;
}

/**
 * Fires `onRendered` once the drawer has rendered OPEN and survived that
 * commit — the owner's "re-arm the one-remount bound" signal (layout.tsx
 * `rearmDrawer`). Handed to the drawer as `renderedSentinel` and rendered
 * INSIDE its open content (account-drawer.tsx), not next to it: the drawer
 * mounts as a closed shell, its hash-open effect then schedules
 * `setOpen(true)` as a DefaultLane update that renders in a later Scheduler
 * task, and only that render evaluates the open body (tabs, forms). A sibling
 * in the owner's tree committed with the shell — before the open render, and
 * before any microtask — so a deterministic crash in the open body re-armed
 * the remount bound on every tap and the `/account` escalation never fired
 * (PR #1141 round 5; the round-4 sibling sentinel). Inside the open content
 * the sentinel commits only when the open render succeeded: a render throw
 * caught by the `OverlayBoundary` discards the whole subtree before commit.
 *
 * The one-microtask deferral covers the EFFECT phase of that commit. A throw
 * in the open body's own passive effects is caught after the commit — React
 * runs the remaining effects of the commit (this one included) and only then
 * schedules the boundary's SyncLane error update, at the end of the
 * passive-effects flush, which is before microtasks. Firing on the mount
 * commit would therefore re-arm for a drawer about to be unmounted as failed;
 * deferred, the failing commit's sentinel is cleaned up (`live` false) by the
 * time the microtask runs. Renders nothing, SSR or client.
 *
 * The header twins host it the same way (round 7): `AccountFlyout` and
 * `AccountMenuDrawer` in hec-header.tsx hand it to their lazy overlays as
 * `renderedSentinel`, rendered inside `PopoverContent` / `DrawerContent`, so
 * their `remountAttempted` ref re-arms when the remounted overlay renders
 * open — not only on a later trigger click, which a hover-opened flyout may
 * never get.
 *
 * Lives here (not in layout.tsx, its first production owner) so the Harness
 * in `account-drawer-retry.test.tsx` exercises THIS component rather than a
 * copy that could drift — layout.tsx imports `cloudflare:workers` and cannot
 * be unit-tested.
 */
export function DrawerRendered({ onRendered }: { onRendered: () => void }) {
  useEffect(() => {
    let live = true;
    queueMicrotask(() => {
      if (live) onRendered();
    });
    return () => {
      live = false;
    };
  }, [onRendered]);
  return null;
}
